import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import HeaderBar from '../components/HeaderBar';
import './BookingHistoryPage.css';

function groupBookings(bookings) {
    const today = new Date().toISOString().split('T')[0];
    const upcoming = [];
    const past = [];

    bookings.forEach((booking) => {
        if (booking.status !== 'confirmed') {
            past.push(booking);
            return;
        }
        if (booking.booking_date >= today) {
            upcoming.push(booking);
        } else {
            past.push(booking);
        }
    });

    const sortByDateAsc = (list) => [...list].sort((a, b) => a.booking_date.localeCompare(b.booking_date));
    const sortByDateDesc = (list) => [...list].sort((a, b) => b.booking_date.localeCompare(a.booking_date));

    return {
        upcoming: sortByDateAsc(upcoming),
        past: sortByDateDesc(past),
    };
}

function BookingHistoryPage({ user, onLogout }) {
    const [bookings, setBookings] = useState([]);
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchBookings = async () => {
            try {
                const response = await axios.get(`/api/bookings/user/${user.email}`);
                setBookings(response.data);
            } catch (err) {
                setError(err.response?.data?.error || 'Failed to load bookings');
            } finally {
                setLoading(false);
            }
        };

        fetchBookings();
    }, [user.email]);

    const { upcoming, past } = useMemo(() => groupBookings(bookings), [bookings]);

    return (
        <div className="booking-history-page">
            <HeaderBar user={user} onLogout={onLogout} />

            <div className="history-container">
                {error && <div className="history-banner error">{error}</div>}

                <section className="history-card">
                    <header className="history-header">
                        <div>
                            <h2>Upcoming Bookings</h2>
                            <p>Upcoming and in-progress desk reservations.</p>
                        </div>
                        <span className="badge">{upcoming.length}</span>
                    </header>

                    {loading ? (
                        <div className="history-empty">Loading upcoming bookings...</div>
                    ) : upcoming.length === 0 ? (
                        <div className="history-empty">No upcoming bookings found.</div>
                    ) : (
                        <div className="history-timeline">
                            {upcoming.map((booking) => {
                                const date = new Date(booking.booking_date + 'T00:00:00');
                                const formattedDate = date.toLocaleDateString('en-US', {
                                    weekday: 'short',
                                    day: 'numeric',
                                    month: 'short',
                                    year: 'numeric'
                                });
                                return (
                                    <article key={booking.id} className="timeline-item">
                                        <div className="timeline-date">{formattedDate}</div>
                                        <div className="timeline-content-horizontal">
                                            <span className="booking-desk">Desk {booking.desk_id}</span>
                                            <span className="booking-divider">•</span>
                                            <span className="booking-location">{booking.zone || 'Zone'} • {booking.unit || 'Unit'}</span>
                                            <span className="booking-divider">•</span>
                                            <span className="booking-time">
                                                {booking.start_time && booking.end_time
                                                    ? `${booking.start_time} - ${booking.end_time}`
                                                    : 'Full day'}
                                            </span>
                                        </div>
                                    </article>
                                );
                            })}
                        </div>
                    )}
                </section>

                <section className="history-card">
                    <header className="history-header">
                        <div>
                            <h2>Booking History</h2>
                            <p>All previous reservations including cancelled records.</p>
                        </div>
                        <span className="badge">{past.length}</span>
                    </header>

                    {loading ? (
                        <div className="history-empty">Loading history...</div>
                    ) : past.length === 0 ? (
                        <div className="history-empty">No past bookings tracked yet.</div>
                    ) : (
                        <table className="history-table">
                            <thead>
                                <tr>
                                    <th>Date</th>
                                    <th>Desk</th>
                                    <th>Zone</th>
                                    <th>Unit</th>
                                    <th>Time</th>
                                    <th>Status</th>
                                </tr>
                            </thead>
                            <tbody>
                                {past.map((booking) => {
                                    const date = new Date(booking.booking_date + 'T00:00:00');
                                    const formattedDate = date.toLocaleDateString('en-US', {
                                        weekday: 'short',
                                        day: 'numeric',
                                        month: 'short',
                                        year: 'numeric'
                                    });
                                    return (
                                        <tr key={booking.id}>
                                            <td>{formattedDate}</td>
                                            <td>{booking.desk_id}</td>
                                            <td>{booking.zone || '—'}</td>
                                            <td>{booking.unit || '—'}</td>
                                            <td>
                                                {booking.start_time && booking.end_time
                                                    ? `${booking.start_time} - ${booking.end_time}`
                                                    : 'Full day'}
                                            </td>
                                            <td className={`status ${booking.status}`}>
                                                {booking.status.replace(/_/g, ' ')}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    )}
                </section>
            </div>
        </div>
    );
}

export default BookingHistoryPage;
